import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { CrownIcon, MailPlusIcon, MoreHorizontalIcon, UsersIcon } from 'lucide-react';
import { PERMISSIONS, ROLE_KEYS } from '@siteflow/shared';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { CopyField } from '@/components/copy-field';
import { DataTable, dataTableColumnHelper, type DataTableColumn } from '@/components/data-table';
import { EmptyState } from '@/components/empty-state';
import { PageHeader } from '@/components/page-header';
import { PermissionGate } from '@/components/permission-gate';
import { StatusChip } from '@/components/status-chip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { messageForError } from '@/lib/api-client';
import { invitationsQuery, memberKeys, membersApi, membersQuery, rolesQuery } from '@/lib/api/members';
import type { Invitation, Member } from '@/lib/api/types';
import { useOrgPermissions } from '@/lib/permissions';
import { formatDate, formatDateTime, initialsFrom } from '@/lib/utils';

const helper = dataTableColumnHelper<Member>();

const inviteSchema = z.object({
  email: z.email('Enter a valid email address'),
  roleKey: z.string().min(1, 'Pick a role'),
});
type InviteValues = z.infer<typeof inviteSchema>;

function TeamPage() {
  const { orgId } = Route.useParams();
  const queryClient = useQueryClient();
  const { has } = useOrgPermissions(orgId);

  const members = useQuery(membersQuery(orgId));
  const invitations = useQuery(invitationsQuery(orgId));
  const roles = useQuery(rolesQuery(orgId));

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Member | null>(null);
  const [transferTarget, setTransferTarget] = useState<Member | null>(null);

  const canManage = has(PERMISSIONS.ORG_MEMBERS_MANAGE);
  const canRemove = has(PERMISSIONS.ORG_MEMBERS_REMOVE);
  const canTransfer = has(PERMISSIONS.ORG_OWNERSHIP_TRANSFER);

  function invalidateMembers() {
    void queryClient.invalidateQueries({ queryKey: memberKeys.all(orgId) });
  }

  const roleOptions = useMemo(
    () => (roles.data ?? []).filter((role) => role.key !== ROLE_KEYS.OWNER),
    [roles.data],
  );

  const inviteForm = useForm<InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: '', roleKey: ROLE_KEYS.EDITOR },
  });

  const inviteMutation = useMutation({
    mutationFn: (values: InviteValues) => membersApi.invite(orgId, values),
    onSuccess: (invitation) => {
      toast.success(`Invitation sent to ${invitation.email}`);
      setInviteLink(invitation.inviteUrl);
      inviteForm.reset({ email: '', roleKey: ROLE_KEYS.EDITOR });
      invalidateMembers();
    },
    onError: (error) => toast.error(messageForError(error, 'We could not send that invitation.')),
  });

  const roleMutation = useMutation({
    mutationFn: ({ memberId, roleKey }: { memberId: string; roleKey: string }) =>
      membersApi.update(orgId, memberId, { roleKeys: [roleKey] }),
    onSuccess: () => {
      toast.success('Role updated');
      invalidateMembers();
    },
    onError: (error) => toast.error(messageForError(error, 'We could not change that role.')),
  });

  const statusMutation = useMutation({
    mutationFn: ({ memberId, status }: { memberId: string; status: 'active' | 'suspended' }) =>
      membersApi.update(orgId, memberId, { status }),
    onSuccess: () => {
      toast.success('Member status updated');
      invalidateMembers();
    },
    onError: (error) => toast.error(messageForError(error, 'We could not update that member.')),
  });

  const removeMutation = useMutation({
    mutationFn: (memberId: string) => membersApi.remove(orgId, memberId),
    onSuccess: () => {
      toast.success('Member removed');
      setRemoveTarget(null);
      invalidateMembers();
    },
    onError: (error) => toast.error(messageForError(error, 'We could not remove that member.')),
  });

  const revokeMutation = useMutation({
    mutationFn: (invitationId: string) => membersApi.revokeInvitation(orgId, invitationId),
    onSuccess: () => {
      toast.success('Invitation revoked');
      invalidateMembers();
    },
    onError: (error) => toast.error(messageForError(error, 'We could not revoke that invitation.')),
  });

  const transferMutation = useMutation({
    mutationFn: (memberId: string) => membersApi.transferOwnership(orgId, { memberId }),
    onSuccess: () => {
      toast.success('Ownership transferred');
      setTransferTarget(null);
      invalidateMembers();
    },
    onError: (error) => toast.error(messageForError(error, 'We could not transfer ownership.')),
  });

  const columns = useMemo<Array<DataTableColumn<Member>>>(
    () =>
      helper.columns([
        helper.accessor('name', {
          header: 'Member',
          cell: (info) => {
            const member = info.row.original;
            return (
              <div className="flex items-center gap-2.5">
                <Avatar>
                  {member.avatarUrl ? <AvatarImage src={member.avatarUrl} alt="" /> : null}
                  <AvatarFallback>{initialsFrom(member.name, member.email)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate font-medium text-ink">{member.name}</span>
                    {member.isOwner ? (
                      <Badge variant="accent">
                        <CrownIcon className="size-3" />
                        Owner
                      </Badge>
                    ) : null}
                  </div>
                  <span className="block truncate font-mono text-micro text-ink-subtle">
                    {member.email}
                  </span>
                </div>
              </div>
            );
          },
        }),
        helper.accessor('roleKeys', {
          header: 'Roles',
          cell: (info) => {
            const member = info.row.original;
            const current = member.roleKeys[0] ?? ROLE_KEYS.VIEWER;

            if (!canManage || member.isOwner) {
              return (
                <div className="flex flex-wrap gap-1">
                  {member.roleKeys.map((key) => (
                    <Badge key={key} variant="neutral">
                      {roles.data?.find((role) => role.key === key)?.name ?? key}
                    </Badge>
                  ))}
                </div>
              );
            }

            return (
              <Select
                value={current}
                onValueChange={(roleKey) => roleMutation.mutate({ memberId: member.id, roleKey })}
                disabled={roleMutation.isPending}
              >
                <SelectTrigger className="h-8 w-40" aria-label={`Role for ${member.name}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((role) => (
                    <SelectItem key={role.key} value={role.key}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          },
        }),
        helper.accessor('status', {
          header: 'Status',
          cell: (info) =>
            info.getValue() === 'active' ? (
              <StatusChip tone="active">Active</StatusChip>
            ) : (
              <StatusChip tone="pending">Suspended</StatusChip>
            ),
        }),
        helper.accessor('lastLoginAt', {
          header: 'Last login',
          cell: (info) => (
            <span className="text-ink-muted" data-numeric>
              {formatDateTime(info.getValue())}
            </span>
          ),
        }),
        helper.accessor('joinedAt', {
          header: 'Joined',
          cell: (info) => (
            <span className="text-ink-muted" data-numeric>
              {formatDate(info.getValue())}
            </span>
          ),
        }),
        helper.display({
          id: 'actions',
          header: () => <span className="sr-only">Actions</span>,
          cell: (info) => {
            const member = info.row.original;
            if (member.isOwner || (!canManage && !canRemove && !canTransfer)) return null;

            return (
              <div className="flex justify-end">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="iconSm" aria-label={`Actions for ${member.name}`}>
                      <MoreHorizontalIcon />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>{member.name}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {canManage ? (
                      <DropdownMenuItem
                        onSelect={() =>
                          statusMutation.mutate({
                            memberId: member.id,
                            status: member.status === 'active' ? 'suspended' : 'active',
                          })
                        }
                      >
                        {member.status === 'active' ? 'Suspend access' : 'Restore access'}
                      </DropdownMenuItem>
                    ) : null}
                    {canTransfer ? (
                      <DropdownMenuItem onSelect={() => setTransferTarget(member)}>
                        <CrownIcon />
                        Transfer ownership
                      </DropdownMenuItem>
                    ) : null}
                    {canRemove ? (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem destructive onSelect={() => setRemoveTarget(member)}>
                          Remove from organisation
                        </DropdownMenuItem>
                      </>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          },
        }),
      ]),
    [canManage, canRemove, canTransfer, roleMutation, roleOptions, roles.data, statusMutation],
  );

  const pendingInvitations = useMemo(
    () => (invitations.data ?? []).filter((i: Invitation) => !i.acceptedAt && !i.revokedAt),
    [invitations.data],
  );

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Organisation"
        title="Team"
        description="Everyone with access to this workspace, the role they hold, and the invitations still outstanding."
        actions={
          <PermissionGate permission={PERMISSIONS.ORG_MEMBERS_INVITE}>
            <Button
              onClick={() => {
                setInviteLink(null);
                setInviteOpen(true);
              }}
            >
              <MailPlusIcon />
              Invite member
            </Button>
          </PermissionGate>
        }
      />

      <section className="flex flex-col gap-3">
        <h2 className="section-label">
          Members{members.data ? ` · ${members.data.length}` : ''}
        </h2>
        <DataTable
          columns={columns}
          data={members.data ?? []}
          isLoading={members.isLoading}
          getRowId={(row) => row.id}
          caption="Organisation members"
          error={
            members.isError ? (
              <span className="text-data text-danger">
                {messageForError(members.error, 'We could not load the team.')}
              </span>
            ) : undefined
          }
          empty={
            <span className="text-data text-ink-muted">
              No members yet — invitations appear here once accepted.
            </span>
          }
        />
      </section>

      <PermissionGate permission={PERMISSIONS.ORG_MEMBERS_VIEW}>
        <section className="flex flex-col gap-3">
          <h2 className="section-label">Pending invitations</h2>
          {pendingInvitations.length === 0 ? (
            <EmptyState
              icon={UsersIcon}
              title="No invitations outstanding"
              description="Invited people appear here until they accept. You can copy their link or revoke it at any time."
            />
          ) : (
            <Card>
              <CardContent className="flex flex-col gap-0 pt-5">
                {pendingInvitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="flex flex-wrap items-center gap-3 border-b border-hairline py-3 first:pt-0 last:border-b-0 last:pb-0"
                  >
                    <div className="min-w-48 flex-1">
                      <p className="truncate font-mono text-data text-ink">{invitation.email}</p>
                      <p className="text-micro text-ink-subtle">
                        Expires {formatDate(invitation.expiresAt)}
                      </p>
                    </div>
                    <Badge variant="neutral">{invitation.roleKey}</Badge>
                    <PermissionGate permission={PERMISSIONS.ORG_MEMBERS_INVITE}>
                      <Button
                        variant="outlineDanger"
                        size="sm"
                        disabled={revokeMutation.isPending}
                        onClick={() => revokeMutation.mutate(invitation.id)}
                      >
                        Revoke
                      </Button>
                    </PermissionGate>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </section>
      </PermissionGate>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite a teammate</DialogTitle>
            <DialogDescription>
              They receive an email with a single-use link. You can also copy the link and share it
              yourself.
            </DialogDescription>
          </DialogHeader>

          <Form {...inviteForm}>
            <form
              noValidate
              onSubmit={inviteForm.handleSubmit((values) => inviteMutation.mutate(values))}
            >
              <DialogBody className="flex flex-col gap-4">
                <FormField
                  control={inviteForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email address</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="teammate@company.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={inviteForm.control}
                  name="roleKey"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Pick a role" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {roleOptions.map((role) => (
                            <SelectItem key={role.key} value={role.key}>
                              {role.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {inviteLink ? (
                  <CopyField label="Invitation link" value={inviteLink} />
                ) : null}
              </DialogBody>

              <DialogFooter>
                <Button variant="secondary" onClick={() => setInviteOpen(false)}>
                  Close
                </Button>
                <Button type="submit" disabled={inviteMutation.isPending}>
                  {inviteMutation.isPending ? 'Sending…' : 'Send invitation'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        destructive
        title="Remove this member?"
        confirmLabel="Remove member"
        pending={removeMutation.isPending}
        description={
          <>
            <strong className="text-ink">{removeTarget?.name}</strong> immediately loses access to
            every site in this organisation. Content they authored is kept.
          </>
        }
        onConfirm={() => removeTarget && removeMutation.mutate(removeTarget.id)}
      />

      <ConfirmDialog
        open={transferTarget !== null}
        onOpenChange={(open) => !open && setTransferTarget(null)}
        title="Transfer ownership?"
        confirmLabel="Transfer ownership"
        pending={transferMutation.isPending}
        description={
          <>
            <strong className="text-ink">{transferTarget?.name}</strong> becomes the owner of this
            organisation, including billing and deletion rights. You keep Admin access. This cannot
            be undone by you alone.
          </>
        }
        onConfirm={() => transferTarget && transferMutation.mutate(transferTarget.id)}
      />
    </div>
  );
}

export const Route = createFileRoute('/app/$orgId/team')({
  component: TeamPage,
});
